// All n8n webhook calls funnel through here.
// Per CLAUDE.md §6, the SPA NEVER mutates ticket-bearing tables directly — it posts intents
// to n8n which dispatches into the shared action-dispatcher subworkflow.

import { supabase } from './supabase';
import { env } from './env';
import { assertActionAccepted } from './ticket-actions';
import type { RuleCondition, RuleAction, RuleMatchType } from './rule-engine';

async function authedFetch<T = unknown>(
  url: string,
  body: unknown,
  opts?: { timeoutMs?: number },
): Promise<T> {
  if (!url) {
    throw new Error('Webhook URL is not configured. Check VITE_*_WEBHOOK_URL env vars.');
  }
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Not signed in');

  // Optional deadline. Ticket actions show their result before the server
  // confirms (lib/ticket-actions.ts), so a request that hangs would leave an
  // unconfirmed change on screen indefinitely.
  const controller = opts?.timeoutMs ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), opts?.timeoutMs) : null;
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      signal: controller?.signal,
    });
  } catch (err) {
    if (controller?.signal.aborted) {
      throw new Error('Request timeout: the server took too long to answer');
    }
    throw err;
  } finally {
    if (timer) clearTimeout(timer);
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status}: ${text || res.statusText}`);
  }

  // Some webhooks return 204/empty body — guard against JSON parse errors.
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

export type TicketAction =
  | 'claim'
  | 'resolve'
  | 'escalate'
  | 'reassign'
  // Snooze family — see dispatcher's `computeSnoozeUntil()` for the math
  // each preset resolves to. `snooze_custom` requires `payload.custom_until`
  // (ISO 8601 string, must be in the future). `unsnooze` is the manual
  // wake-up (the auto-resurface pg_cron also clears the column when the
  // timestamp passes — same end state).
  | 'snooze_1h'
  | 'snooze_4h'
  | 'snooze_tomorrow'
  | 'snooze_monday'
  | 'snooze_2d'
  | 'snooze_custom'
  | 'unsnooze'
  | 'merge'
  | 'archive'
  | 'unarchive'
  | 'close'
  // Added for automation rules (Phase 2 wires these into the dispatcher).
  // set_priority sets an absolute priority (payload.priority = 'P1'..'P4');
  // add_watcher adds a watcher (payload.watcher_slack_id); add_note appends
  // an internal note (payload.note). assign reuses 'reassign'; dismiss reuses
  // 'close'; snooze reuses the snooze_* family.
  | 'set_priority'
  | 'add_watcher'
  | 'add_note'
  // reopen undoes a close/resolve (restore status, clear resolved/archived).
  // Powers automation revert. payload.status optional (defaults in_progress).
  | 'reopen';

export interface ActionIntent {
  action: TicketAction;
  ticket_id: string;
  payload?: Record<string, unknown>;
}

/** Outbound attachment uploaded to Supabase Storage by the composer.
 *  Workflow 12/15 fetches each url and embeds the bytes as multipart/mixed
 *  parts in the outgoing MIME message. */
export interface OutboundAttachment {
  filename: string;
  contentType: string;
  size_bytes: number;
  url: string;
}

export interface ReplyIntent {
  ticket_id: string;
  /** Plain-text body. Always sent. Used for the text/plain MIME part and
   *  the ticket_messages.body column. */
  body: string;
  /** Optional rich HTML body. When provided, workflow 12 uses this
   *  verbatim for the text/html MIME part instead of generating one from
   *  `body`. Carries inline-image URLs from the rich composer. */
  body_html?: string;
  /** Optional override of the default To (the original sender). Used by Reply All / Forward / custom. */
  to?: string[];
  cc?: string[];
  bcc?: string[];
  /** Optional subject override (used by Forward, which deliberately starts a new thread). */
  subject?: string;
  /**
   * Per-click UUID generated by the composer (migration 026). Workflow 12 checks for a
   * prior ticket_messages row with this key and short-circuits before calling Gmail again
   * — guards against double-click + retry-on-network-blip duplicate sends.
   */
  idempotency_key?: string;
  /** Files uploaded to Supabase Storage by the composer; workflow 12 fetches + embeds. */
  attachments?: OutboundAttachment[];
}

export interface JourneyExportIntent {
  client_email: string;
}

/** Workflow 16 (leave-notify) trigger — fans out a Slack DM to every
 *  active admin so they see the request without needing the app open.
 *  Best-effort: the SPA fires this after a successful leave-request
 *  INSERT and doesn't propagate errors (the in-app notification
 *  fan-out via `notify_admins_leave_requested` is the primary path). */
export interface LeaveNotifyIntent {
  period_id: string;
}

/** Workflow 17 (customer-assist) — start a live session. The agent
 *  describes what they need (`agent_purpose`), the workflow mints a
 *  token, inserts the customer_assist_sessions row, emails the client
 *  the magic link, and writes a ticket_event. Response includes the
 *  session_id and magic link so the agent UI can show them. */
export interface AssistStartIntent {
  ticket_id: string;
  agent_purpose: string;
}

export interface AssistStartResponse {
  ok: boolean;
  session_id?: string;
  token?: string;
  magic_link?: string;
  /** ISO timestamp at which the pg_cron expire job will flip this
   *  session to `expired` if no one explicitly ends it first. The
   *  agent widget renders this as a countdown — typically 30 min
   *  from start (migration 047 default). */
  expires_at?: string;
  /** Set when there's already a pending/active session for this ticket.
   *  The agent UI offers "Open existing" instead of erroring out. */
  existing_session_id?: string;
  error?: string;
}

/** End an active session. Posts the transcript (which the SPA assembled
 *  from the local in-memory buffer, sans sensitive values) so workflow 17
 *  can write it as a ticket_notes row and update the session status. */
export interface AssistEndIntent {
  session_id: string;
  /** Plain-text transcript built client-side. Sensitive exchanges are
   *  already redacted in the buffer (`[code shared — not retained]`). */
  transcript: string;
}

/** Payload for "compose new ticket" — workflow 15. Sends a fresh Gmail
 * thread to a customer + creates the ticket row. */
export interface CreateTicketIntent {
  /** The inbox the agent is composing from (the inbox they're viewing).
   *  Workflow 15 sends FROM this inbox, files the ticket under it, and
   *  respects its slack_enabled toggle. Omitted when composing from a
   *  non-inbox page → workflow 15 falls back to the primary inbox. */
  inbox_id?: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  /** Plain-text body — always sent. Workflow 15 uses it for the text/plain
   *  MIME part and `ticket_messages.body`. */
  body: string;
  /** Optional rich HTML body. When provided, workflow 15 uses this verbatim
   *  for the text/html MIME part instead of generating one from `body`. */
  body_html?: string;
  priority?: 'P1' | 'P2' | 'P3' | 'P4';
  category?: string;
  /** Per-click UUID. Same dedupe pattern as send-reply (migration 026). */
  idempotency_key?: string;
  attachments?: OutboundAttachment[];
}

export interface CreateTicketResponse {
  ok: boolean;
  ticket_id?: string;
  gmail_message_id?: string;
  gmail_thread_id?: string;
  deduped?: boolean;
  error?: string;
}

/** How long a ticket action may take before the screen gives up and puts the
 *  ticket back. n8n normally answers in 1-3 s. */
const ACTION_TIMEOUT_MS = 45_000;

/** Posts a ticket action to the dispatcher (workflow 11). Resolves with the
 *  reply (`{ ok, updated }`, where `updated` is the new ticket row). Rejects
 *  on a non-2xx AND on the dispatcher's 200-with-`ok:false` refusal, which a
 *  bare status check would report as success. */
export const postAction = (intent: ActionIntent) =>
  authedFetch(env.VITE_ADMIN_ACTION_WEBHOOK_URL, intent, { timeoutMs: ACTION_TIMEOUT_MS }).then(
    assertActionAccepted,
  );

/** How long one send attempt may take before the outbox marks it failed and
 *  offers Retry. Longer than an action: workflow 12 sends through Gmail, then
 *  records the message and posts to Slack. Retrying reuses the idempotency key,
 *  so an attempt that did go through is deduped rather than sent twice. */
const SEND_TIMEOUT_MS = 120_000;

export const postReply = (reply: ReplyIntent) =>
  authedFetch(env.VITE_SEND_REPLY_WEBHOOK_URL, reply, { timeoutMs: SEND_TIMEOUT_MS });

/** What workflow 13 replies with. It is synchronous: the journey row already
 *  exists when this returns. `deduped` = the same client was exported in the
 *  last 24h, so the existing journey is returned instead of a new one. */
export interface JourneyExportResponse {
  ok: boolean;
  journey_id?: string;
  deduped?: boolean;
  ticket_count?: number;
  client_email?: string;
}

export const postJourneyExport = (req: JourneyExportIntent) =>
  authedFetch<JourneyExportResponse>(env.VITE_JOURNEY_EXPORT_WEBHOOK_URL, req);

export const postCreateTicket = (req: CreateTicketIntent) =>
  authedFetch<CreateTicketResponse>(env.VITE_CREATE_TICKET_WEBHOOK_URL, req, {
    timeoutMs: SEND_TIMEOUT_MS,
  });

/** Trigger workflow 16 (Slack DM fan-out to admins on a new leave
 *  request). Callers should swallow rejections — this is decorative. */
export const postLeaveNotify = (req: LeaveNotifyIntent) =>
  authedFetch(env.VITE_LEAVE_NOTIFY_WEBHOOK_URL, req);

/** Start a Customer Assist session (workflow 17, action=start). */
export const postAssistStart = (req: AssistStartIntent) =>
  authedFetch<AssistStartResponse>(env.VITE_ASSIST_WEBHOOK_URL, { action: 'start', ...req });

/** End an active Customer Assist session (workflow 17, action=end). */
export const postAssistEnd = (req: AssistEndIntent) =>
  authedFetch(env.VITE_ASSIST_WEBHOOK_URL, { action: 'end', ...req });

// Supabase Edge Functions are siblings of the project, not n8n webhooks.
// We construct the URL from VITE_SUPABASE_URL (already in env) and reuse the
// same Bearer-JWT auth pattern.
function fnUrl(name: string): string {
  return `${env.VITE_SUPABASE_URL.replace(/\/$/, '')}/functions/v1/${name}`;
}

export interface DraftReplyResponse {
  ok: boolean;
  draft?: string;
  error?: string;
  /** What the draft was grounded in, so the UI can say so. */
  context?: { notes: number; kb: number };
}

export interface DraftReplyRequest {
  /** Omit for a brand-new Compose email (no ticket yet). */
  ticketId?: string;
  /** Short steer typed in the AI bar: "tell them the refund is approved". */
  instruction?: string;
  /** Rough notes the agent already typed; used as the brief, not discarded. */
  agentNotes?: string;
  /** Compose only: display hint for the model. */
  recipient?: string;
}

export const postDraftReply = (req: DraftReplyRequest) =>
  authedFetch<DraftReplyResponse>(fnUrl('draft-reply'), {
    ticket_id: req.ticketId,
    instruction: req.instruction || undefined,
    agent_notes: req.agentNotes || undefined,
    mode: req.ticketId ? 'reply' : 'compose',
    recipient: req.recipient || undefined,
  });

export interface SyncInboxToN8nResponse {
  ok: boolean;
  /** True when this call created the n8n Gmail OAuth credential. */
  credential_created?: boolean;
  /** True when this call inserted the trigger + tag nodes into workflow 01. */
  trigger_added?: boolean;
  credential_id?: string;
  workflow_id?: string;
  error?: string;
}

/** Calls the sync-inbox-to-n8n Edge Function. Admin-gated server-side. */
export const postSyncInboxToN8n = (inboxId: string) =>
  authedFetch<SyncInboxToN8nResponse>(fnUrl('sync-inbox-to-n8n'), { inbox_id: inboxId });

export interface PolishReplyResponse {
  ok: boolean;
  polished?: string;
  error?: string;
}

/** One-click restyle presets understood by polish-reply. Keep in sync with
 *  STYLE_PRESETS in supabase/functions/_shared/reply-assist.ts. */
export type PolishPreset =
  | 'fix_grammar'
  | 'shorter'
  | 'friendlier'
  | 'formal'
  | 'firmer'
  | 'plain';

export interface PolishReplyRequest {
  /** Omit from the Compose dialog (no ticket yet). */
  ticketId?: string;
  draft: string;
  preset?: PolishPreset;
  instruction?: string;
}

export const postPolishReply = (req: PolishReplyRequest) =>
  authedFetch<PolishReplyResponse>(fnUrl('polish-reply'), {
    ticket_id: req.ticketId,
    draft: req.draft,
    preset: req.preset,
    instruction: req.instruction || undefined,
  });

export interface DraftAssistFollowupResponse {
  ok: boolean;
  draft?: string;
  error?: string;
}

/** Draft a follow-up email body for a just-ended Customer Assist
 *  session. The Edge Function reads the persisted transcript from
 *  `customer_assist_messages` for the session_id, formats it the
 *  same way the SPA's `buildTranscript()` does, and asks Claude
 *  to write a 1-2 paragraph recap. The result lands in the Reply
 *  Composer via the same sessionStorage prefill plumbing as the
 *  static template path — the agent reviews + edits + sends. */
export const postDraftAssistFollowup = (ticketId: string, sessionId: string) =>
  authedFetch<DraftAssistFollowupResponse>(
    fnUrl('draft-assist-followup'),
    { ticket_id: ticketId, session_id: sessionId },
  );

export interface DaySummaryResponse {
  ok: boolean;
  summary?: string;
  ticket_count?: number;
  total_found?: number;
  truncated?: boolean;
  generated_at?: string;
  /** Echoed back so the SPA can label the result card "Summary for
   *  X" when an admin is viewing another team member's recap. */
  subject_slack_id?: string;
  subject_name?: string;
  viewer_is_subject?: boolean;
  error?: string;
}

/** Generate an end-of-day (or end-of-range) recap of every ticket
 *  the named agent touched in the window. Backed by the
 *  `generate-day-summary` Edge Function (Claude Haiku). Output is
 *  plain-text prose formatted with em-dash markers — render with
 *  `whitespace-pre-wrap` for line-break fidelity.
 *
 *  @param subjectSlackId  Optional. Defaults to the signed-in
 *    agent. Admins can pass another active team member's
 *    slack_user_id to view their recap; non-admins get 403 from
 *    the server if they try. */
export const postGenerateDaySummary = (
  fromIso: string,
  toIso: string,
  subjectSlackId?: string,
) =>
  authedFetch<DaySummaryResponse>(
    fnUrl('generate-day-summary'),
    subjectSlackId
      ? { from: fromIso, to: toIso, subject_slack_id: subjectSlackId }
      : { from: fromIso, to: toIso },
  );

// ─── Automation rules: AI authoring (admin-only, server-enforced) ────────────
// Both call the `automation-rule-ai` Edge Function. Neither applies anything —
// they only return a draft the admin reviews in the /automations editor.

/** A structured rule draft as returned by the parser. Conditions/actions reuse
 *  the shared engine types so the editor + dry-run consume one shape. */
export interface ParsedRule {
  name: string;
  match_type: RuleMatchType;
  conditions: RuleCondition[];
  actions: RuleAction[];
  explanation: string;
  warnings: string[];
}

export interface ParseRuleResponse {
  ok: boolean;
  rule?: ParsedRule;
  error?: string;
}

export interface EnhanceRuleResponse {
  ok: boolean;
  enhanced?: string;
  suggestions?: string[];
  error?: string;
}

/** Prompt enhancer: tighten the admin's rough description + list clarifications. */
export const postEnhanceRulePrompt = (description: string) =>
  authedFetch<EnhanceRuleResponse>(fnUrl('automation-rule-ai'), { mode: 'enhance', description });

/** Refine an AI-judgement test (the per-ticket yes/no instruction) specifically. */
export const postEnhanceAiTest = (instruction: string) =>
  authedFetch<EnhanceRuleResponse>(fnUrl('automation-rule-ai'), { mode: 'enhance_test', description: instruction });

/** Parse a description into a structured {conditions, actions, explanation, warnings}. */
export const postParseRule = (description: string) =>
  authedFetch<ParseRuleResponse>(fnUrl('automation-rule-ai'), { mode: 'parse', description });

// ─── AI judgement: classify a batch of tickets against a plain-English test ───
export interface ClassifyVerdict {
  id: string;
  match: boolean;
  reason: string;
}
export interface ClassifyResponse {
  ok: boolean;
  results?: ClassifyVerdict[];
  error?: string;
}

/** Decide a yes/no test against a batch of tickets (≤25). Never mutates. */
export const postClassifyTickets = (instruction: string, tickets: unknown[]) =>
  authedFetch<ClassifyResponse>(fnUrl('classify-tickets'), { instruction, tickets });
